import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name for the dialog, announced on open. */
  title: string;
  children: ReactNode;
  className?: string;
}

/**
 * Accessible modal dialog.
 *
 * The original build injected raw markup into <body> with no dialog role,
 * no keyboard handling and no focus management. This restores all three:
 * Escape closes, focus moves in and is returned on close, background scroll
 * is locked, and screen readers announce it as a dialog.
 */
export function Modal({ open, onClose, title, children, className }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    // Remember what was focused so it can be restored on close.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);

    // Lock background scroll while the dialog owns the screen.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-100 grid place-items-center overflow-y-auto bg-overlay p-4 backdrop-blur-sm"
      // Only a click that starts and ends on the scrim dismisses, so a drag
      // out of the dialog does not close it.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'w-full max-w-lg rounded-panel border border-border bg-surface p-5 shadow-[0_24px_60px_rgba(0,0,0,0.5)] outline-none',
          className,
        )}
      >
        <h2 id={titleId} className="sr-only">
          {title}
        </h2>
        {children}
      </div>
    </div>,
    document.body,
  );
}
